/**
 * Session Expiry Modal — "Your session is about to expire. Would you like to extend?"
 *
 * Shown on routes that are NOT in the silent-refresh list. Triggered when the
 * JWT is ~10 seconds from expiry.
 *
 * Props:
 *   open        — boolean
 *   countdown   — number, seconds remaining
 *   onExtend    — () => void, called when user clicks "Extend Session"
 *   onLogout    — () => void, called when user clicks "Logout" or countdown hits 0
 */
const SessionExpiryModal = ({ open, countdown, onExtend, onLogout }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expiry-title"
    >
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-2 text-center">
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primaryColor/10 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-primaryColor"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
              />
            </svg>
          </div>
          <h2
            id="session-expiry-title"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            Session about to expire
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Your session is about to expire. Would you like to extend?
          </p>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            You will be logged out in{" "}
            <span className="font-semibold text-primaryColor">{countdown}s</span>
          </p>
        </div>

        <div className="px-6 pb-6 pt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onExtend}
            className="px-4 py-2 rounded-md bg-primaryColor text-white text-sm font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primaryColor focus:ring-offset-2 transition"
          >
            Extend Session
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="px-4 py-2 rounded-md bg-white text-gray-700 border border-gray-300 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiryModal;